import { Theme } from "@material-ui/core";
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import AddIcon from "@material-ui/icons/Add";
import { inject, observer } from "mobx-react";
import queryString from "query-string";
import React, { ReactNode } from "react";
import { RouteComponentProps } from "react-router";
import {
  FindAllParams,
  ListDataSource,
  LocalFindAllParams
} from "../../../dataAccess/DataSource";
import { LoggerFactory } from "../../../logger";
import { Columns } from "../../DataTable/commons";
import { DataTable } from "../../DataTable/DataTable";
import { RelativeButtonLink } from "../../RelativeLink/RelativeLink";
import { Secured, SecurityProvider } from "../../Secured";
import { defaultRowButtons, defaultRowButtonsCreator } from "./RowButtons";
import {
  defaultToolbarButtons,
  defaultToolbarButtonsCreator
} from "./ToolbarButtons";
import { clone } from "lodash";
import { CommonsStore, PopupMessage } from "../../../model/CommonsStore";
import { I18n } from "../../../model/I18n";
import { TitleRow } from "../TitleRow";
import {
  DefaultDataTableContainer,
  DefaultTitleContainer,
  DefaultViewContainer,
  ViewProps
} from "../OneView";
import { RouterStore } from "../../../router/RouterStore";

const log = LoggerFactory.create("IndexView");

/*
 * IndexView viewStyles
 * */
export const indexStyles = (theme: Theme) =>
  createStyles({
    cardContent: {
      padding: "0",
      overflow: "auto"
    },
    title: {},
    titleText: {}
  });

/*
 * IndexView props
 * */
export interface IndexProps<T, ID>
  extends ViewProps,
    WithStyles<typeof indexStyles>,
    RouteComponentProps {
  /** dataSource giving access to data */
  dataSource: ListDataSource<T, ID>;
  /** tittle of the table.*/
  columns: Columns;
  /** overrides row buttons*/
  rowButtons?: any;
  /** overrides toolbar buttons */
  toolbarButtons?: any;
  /** overrides title buttons */
  titleButtons?: (props: IndexProps<T, ID>) => ReactNode;
  /** default params for view that will be added to all params*/
  fixedParams?: FindAllParams;
  /** hide checkboxes */
  disableCheckboxes?: false;
  /** hide pageable component */
  hidePageable?: false;
  /** align cell content, left by default */
  alignCellText?: "center" | "right";
  /** hide total page component */
  hideTotalPages?: false;
  /** levels of rows per page for user to choose from */
  pageSizeOptions?: number[];
  /** show row buttons one after another in row */
  inlineRowButtons?: false;
  /** custom error message optionally mixed with length */
  deleteMessage?: (rows: any) => PopupMessage;
  /** modifying link of default buttons  */
  baseUrl?: string;
  /** commons store injected by mobx */
  commons: CommonsStore;
  /** router store injected by mobx */
  router: RouterStore;
  /** translations */
  i18n: I18n;
  /** container of table body which returns callback */
  tableBodyComponent?: React.ComponentType;
  /** container of table row which corresponds body functionality */
  tableRowComponent?: React.ComponentType;
}

/*
 * IndexView state
 * */
export interface IndexState {
  dialogOpen: boolean;
  selectedRows: any;
  parentData: {};
}

/*
 * Default view for displaying list of entities and
 * contains links and buttons to perform basic operations:
 * - create new entity
 * - see entity details
 * - delete entity
 *
 * Defining extra row buttons.
 * ```
 * export const Index = injectAnnouncements(({announcements}: any) => {
 *   return <IndexView dataSource={announcements.listDataSource}
 *                     rowButtons={extraButtons}
 *                     columns={AnnouncementColumns}/>
 * });
 *
 * const extraButtons = (props: IndexProps<{}, {}>) => (row: any, key: string) => {
 *   const def = defaultRowButtons(props)(row);
 *
 *   return <><MenuItem className="menuItem"><SpeakerNotesTwoToneIcon /></MenuItem>{def}</>
 * };
 *
 * Example use of dragAndDrop
 * ```
 *     const onDnd = (event: any, data: any) => {
 *         console.log("DND", event, data);
 *     };
 *
 *    tableBodyComponent={withDroppable(onDnd)(TableBody)}
 *    tableRowComponent={draggableRow}
 * ```
 * */

@inject("auth", "commons", "i18n", "router")
@observer
export class IndexViewComponent<T, ID> extends React.Component<
  IndexProps<T, ID>,
  IndexState
> {
  public constructor(props: IndexProps<T, ID>) {
    super(props);
    this.state = this.createInitialState();
  }

  public componentDidMount() {
    log.debug("componentDidMount");
    /** Retrieve previous sorting and filtering params from browserHistory.
     * Works if global flag shouldPersistParams is truthy */
    this.props.router.retrievePersistedParams(this.props.location.pathname);
    // todo: before this function returns put component in some initial state (dont show previous state)
    const params = this.prepareParams();
    if (this.props.pageSizeOptions) {
      params.page = { pageSize: this.props.pageSizeOptions[0], pageNumber: 0 };
    }
    this.props.dataSource.findAll(params);
  }

  public componentDidUpdate(prevProps: IndexProps<T, ID>) {
    if (
      this.props.fixedParams !== prevProps.fixedParams ||
      this.props.dataSource !== prevProps.dataSource
    ) {
      log.debug("componentDidUpdate", this);
      const params = this.prepareParams();
      this.props.dataSource.findAll(params);
    } else {
      log.debug(
        "componentDidUpdate (not calling findAll as fixedParams and dataSource did not change)",
        this
      );
    }
  }

  public componentWillUnmount() {
    log.debug("componentWillUnmount()", this);
    this.props.dataSource.clearSelected();
    if (this.props.fixedParams) {
      this.props.dataSource.clear();
    }
    /** Persist query params, so they are available from browserHistory after coming back.
     * Works if global flag shouldPersistParams is truthy */
    this.props.router.persistQueryParams(
      this.props.location.pathname,
      this.props.location.search
    );
  }

  public delete = async () => {
    await this.props.dataSource.delete(this.state.selectedRows);
    this.props.commons.closeDialog();
  };

  public callbackDelete = (rows: any) => {
    this.setState({ selectedRows: rows });
    this.getPopupMessage(rows);
  };

  public render() {
    const { classes, dataSource, columns, titleVariant } = this.props;
    const title = this.props.title || `${dataSource.name}`;

    const ViewContainer = this.props.viewContainer || DefaultViewContainer;
    const TitleContainer = this.props.titleContainer || DefaultTitleContainer;
    const ContentContainer =
      this.props.contentContainer || DefaultDataTableContainer;

    const rowButtons: any = this.createRowButtons();
    const toolbarButtons: any = this.createToolbarButtons();
    const titleButtons = this.createTitleButtons();

    return (
      <SecurityProvider security={this.props.dataSource.schema.security}>
        <ViewContainer>
          <TitleContainer>
            <TitleRow
              title={title}
              titleVariant={titleVariant}
              classes={classes}
              suffix={titleButtons}
            />
          </TitleContainer>

          <ContentContainer>
            <DataTable
              data={dataSource.list}
              total={this.props.dataSource.total}
              disableCheckboxes={this.props.disableCheckboxes}
              hidePageable={this.props.hidePageable}
              alignCellText={this.props.alignCellText}
              hideTotalPages={this.props.hideTotalPages}
              rowButtons={rowButtons}
              toolbarButtons={toolbarButtons}
              inlineRowButtons={this.props.inlineRowButtons}
              pageSizeOptions={this.props.pageSizeOptions}
              params={dataSource.params}
              columns={columns}
              selected={dataSource.selected}
              toggleSelect={this.toggleSelect}
              updateRows={this.updateRows}
              toggleSelectAll={this.toggleSelectAll}
              search={this.search}
              tableBodyComponent={this.props.tableBodyComponent}
              tableRowComponent={this.props.tableRowComponent}
              i18n={this.props.i18n}
            />
          </ContentContainer>
        </ViewContainer>
      </SecurityProvider>
    );
  }

  public prepareParams = (): LocalFindAllParams => {
    const p = this.readParamsFromLink() || { filters: [] };
    this.mergeFixedParams(p);
    return p;
  };

  private getPopupMessage = (rows?: any) => {
    const deleteOneTitle = this.props.i18n.t(
      "lego:views.index.delete.title",
      "Confirm deleting row"
    );
    const deleteOneContent = this.props.i18n.t(
      "lego:views.index.delete.content",
      "Are you sure you want to delete selected row?"
    );
    let dialogMessage: PopupMessage = {
      title: deleteOneTitle,
      content: deleteOneContent
    };
    if (this.props.deleteMessage) {
      dialogMessage = this.props.deleteMessage(rows);
    } else if (rows && rows.length) {
      const deleteManyTitle = this.props.i18n.t(
        "lego:views.index.deleteSelected.title",
        "Confirm deleting {{length}} rows",
        { length: rows.length }
      );
      const deleteManyContent = this.props.i18n.t(
        "lego:views.index.deleteSelected.content",
        "Are you sure you want to delete selected rows?"
      );
      dialogMessage = {
        title: deleteManyTitle,
        content: deleteManyContent
      };
    }
    this.props.commons.showDialog(dialogMessage, this.delete);
  };

  private search = async (params: FindAllParams) => {
    const result = await this.props.dataSource.findAll(params);
    this.persistParamsInLink();
    return result;
  };

  private toggleSelect = (multiSelect: boolean, id: any) => {
    if (multiSelect) {
      this.props.dataSource.toggleSelect(id);
    } else {
      this.props.dataSource.toggleSelectOne(id);
    }
  };

  private updateRows = (ids: ID[] | ID, data: {}) => {
    this.props.dataSource.updateRows(ids, data);
  };

  private toggleSelectAll = () => {
    this.props.dataSource.toggleSelectAll();
  };

  private createInitialState() {
    const selectedRows = {};
    const dialogOpen = false;
    const parentData = {};

    return {
      selectedRows,
      dialogOpen,
      parentData
    };
  }

  private createRowButtons() {
    let rowButtons = {};
    if (typeof this.props.rowButtons === "function") {
      rowButtons = this.props.rowButtons(this);
    } else if (this.props.rowButtons) {
      rowButtons = defaultRowButtonsCreator({ ...this.props.rowButtons })(this);
    } else {
      rowButtons = defaultRowButtons(this);
    }
    return rowButtons;
  }

  private createToolbarButtons() {
    let toolbarButtons = {};
    if (typeof this.props.toolbarButtons === "function") {
      toolbarButtons = this.props.toolbarButtons(this);
    } else if (this.props.toolbarButtons) {
      toolbarButtons = defaultToolbarButtonsCreator({
        ...this.props.toolbarButtons
      })(this);
    } else {
      toolbarButtons = defaultToolbarButtons(this);
    }
    return toolbarButtons;
  }

  private createTitleButtons(): ReactNode {
    let createInitialValuesQuery;
    const urlPrefix = this.props.baseUrl || "";

    if (this.props.fixedParams) {
      const createInitialValues = this.props.fixedParams.filters.reduce(
        (acc, field) => {
          acc[field.field] = field.value;
          return acc;
        },
        {}
      );
      createInitialValuesQuery = queryString.stringify(createInitialValues);
    } else {
      createInitialValuesQuery = "";
    }

    return (
      (this.props.titleButtons && this.props.titleButtons(this.props)) || (
        <Secured>
          <RelativeButtonLink
            to={urlPrefix + "/create?" + createInitialValuesQuery}
            variant="contained"
            color="primary"
            icon={<AddIcon />}
          />
        </Secured>
      )
    );
  }

  private persistParamsInLink() {
    const currentSearch = clone(
      queryString.parse(this.props.location.search)
    ) as any;
    currentSearch.search = JSON.stringify(this.props.dataSource.params);
    log.debug("encoded state", currentSearch.search);
    const searchString = queryString.stringify(currentSearch);
    this.props.history.replace(
      this.props.history.location.pathname + "?" + searchString
    );
  }

  private readParamsFromLink() {
    const search = queryString.parse(this.props.history.location.search);
    if (search.search) {
      const decoded = search.search as string;
      return JSON.parse(decoded);
    } else {
      return null;
    }
  }

  private mergeFixedParams(params: FindAllParams) {
    if (this.props.fixedParams) {
      log.debug("merging", params);
      log.debug("with", this.props.fixedParams);

      const fixedParams = this.props.fixedParams;

      if (fixedParams.page) {
        if (!params.page) {
          params.page = fixedParams.page;
        } else {
          params.page.pageNumber =
            fixedParams.page.pageNumber || params.page.pageNumber;
          params.page.pageSize =
            fixedParams.page.pageSize || params.page.pageSize;
        }
      }

      if (fixedParams.sort) {
        if (!params.sort) {
          params.sort = fixedParams.sort;
        } else {
          params.sort.sort = fixedParams.sort.sort || params.sort.sort;
          params.sort.order = fixedParams.sort.order || params.sort.order;
        }
      }

      if (fixedParams.filters) {
        params.filters.push.apply(params.filters, fixedParams.filters);
      }
    }
  }
}

export const IndexView = withStyles(indexStyles, { withTheme: true })(
  IndexViewComponent
);
